import {
  Box,
  Button,
  styled,
  IconButton,
  Divider,
  Stack,
  Text,
  Checkbox,
  FormControlLabel,
  alpha,
} from '@ltvco/refresh-lib/theme';

export const FormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.grey[200]}`,
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.08)}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    backgroundColor: theme.palette.common.white,
    width: `calc(100% - ${theme.spacing(4)})`,
  },
}));

export const SearchTypeChip = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ theme, isSelected }) => ({
  borderRadius: '100px',
  padding: '6px 16px',
  fontSize: '0.875rem',
  fontWeight: 700,
  textTransform: 'none',
  minHeight: '36px',
  whiteSpace: 'nowrap',
  border: 'none',
  borderColor: isSelected
    ? theme.palette.primary.main
    : theme.palette.grey[300],
  backgroundColor: isSelected
    ? theme.palette.primary.main
    : theme.palette.grey[100],
  color: isSelected ? theme.palette.common.white : theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: isSelected
      ? theme.palette.primary.dark
      : theme.palette.grey[200],
    borderColor: isSelected
      ? theme.palette.primary.dark
      : theme.palette.grey[400],
  },
  '&:disabled': {
    opacity: 0.6,
  },
  transition: 'all 0.2s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    padding: '6px 13px',
    fontSize: '14px',
    minHeight: '34px',
    flexShrink: 0,
  },
}));

export const FilterChip = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  borderRadius: '100px',
  padding: '6px 14px',
  fontSize: '14px',
  fontWeight: 400,
  textTransform: 'none',
  minHeight: '32px',
  border: '1px solid',
  borderColor: isActive
    ? theme.palette.primary.main
    : alpha(theme.palette.common.black, 0.2),
  backgroundColor: isActive
    ? alpha(theme.palette.primary.main, 0.08)
    : theme.palette.common.white,
  color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: isActive
      ? alpha(theme.palette.primary.main, 0.12)
      : theme.palette.grey[50],
    borderColor: isActive
      ? theme.palette.primary.main
      : alpha(theme.palette.common.black, 0.3),
  },
  '&:disabled': {
    opacity: 0.6,
  },
  transition: 'all 0.2s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    padding: '6px 10px',
    fontSize: '12px',
    minHeight: '28px',
  },
}));

export const PillSearchBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '48px',
  backgroundColor: theme.palette.common.white,
  border: '1px solid',
  borderColor: theme.palette.grey[400],
  borderRadius: '1000px',
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2),
  gap: theme.spacing(1.5),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
  },
  [theme.breakpoints.down('md')]: {
    height: '44px',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.5),
  },
  [theme.breakpoints.down('sm')]: {
    height: '50px',
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2),
    gap: theme.spacing(1),
    borderColor: theme.palette.grey[500],
  },
}));

export const SearchInputField = styled('input')(({ theme }) => ({
  flex: 1,
  border: 'none',
  outline: 'none',
  backgroundColor: 'transparent',
  fontSize: '1rem',
  fontWeight: 400,
  color: theme.palette.text.primary,
  fontFamily: 'inherit',
  '&::placeholder': {
    color: theme.palette.text.secondary,
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
}));

export const InfoButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.2),
  padding: theme.spacing(0.5),
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.25rem',
    color: theme.palette.text.secondary,
  },
}));

export const SearchButton = styled(Button)(({ theme }) => ({
  borderRadius: '32px',
  height: '42px',
  minWidth: '110px',
  paddingLeft: '22px',
  paddingRight: '22px',
  fontSize: '15px',
  fontWeight: 700,
  textTransform: 'capitalize',
  letterSpacing: '0.46px',
  marginLeft: theme.spacing(1.5),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  boxShadow: `0px 3px 1px -2px ${alpha(
    theme.palette.common.black,
    0.2
  )}, 0px 2px 2px 0px ${alpha(
    theme.palette.common.black,
    0.14
  )}, 0px 1px 5px 0px ${alpha(theme.palette.common.black, 0.12)}`,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: `0px 3px 3px -2px ${alpha(
      theme.palette.common.black,
      0.2
    )}, 0px 3px 4px 0px ${alpha(
      theme.palette.common.black,
      0.14
    )}, 0px 1px 8px 0px ${alpha(theme.palette.common.black, 0.12)}`,
  },
  '&:disabled': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
    boxShadow: 'none',
  },
  [theme.breakpoints.down('md')]: {
    height: '40px',
    minWidth: '100px',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

export const MobileSearchButton = styled(Button)(({ theme }) => ({
  display: 'none',
  borderRadius: '32px',
  height: '36px',
  width: '100%',
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'capitalize',
  letterSpacing: '0.4px',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  boxShadow: `0px 3px 1px -2px ${alpha(
    theme.palette.common.black,
    0.2
  )}, 0px 2px 2px 0px ${alpha(
    theme.palette.common.black,
    0.14
  )}, 0px 1px 5px 0px ${alpha(theme.palette.common.black, 0.12)}`,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&:disabled': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
    boxShadow: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
  },
}));

export const SearchRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

export const ProTipBox = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  borderRadius: '8px',
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(2.5),
  [theme.breakpoints.down('sm')]: {
    backgroundColor: 'transparent',
    padding: 0,
    marginBottom: theme.spacing(2),
  },
}));

export const FilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
}));

export const FilterChipsContainer = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    display: 'contents',
  },
}));

export const MobileActionsContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  width: '100%',
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
  },
}));

export const FilterDivider = styled(Divider)(({ theme }) => ({
  height: '24px',
  borderColor: theme.palette.divider,
  margin: theme.spacing(0.5, 0.5, 0),
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

export const SearchTypeChipsContainer = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
  flexWrap: 'wrap',
  gap: theme.spacing(1.375),
  rowGap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'nowrap',
    overflowX: 'auto',
    paddingBottom: theme.spacing(1),
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
  },
}));

export const TitleText = styled(Text)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(1.5),
  color: theme.palette.text.primary,
  fontSize: '1.25rem',
  lineHeight: 'inherit',
  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
    lineHeight: '24px',
  },
}));

export const ProTipText = styled(Text)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  lineHeight: 'inherit',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    lineHeight: '24px',
  },
}));

export const ProTipLabel = styled('span')(({ theme }) => ({
  color: theme.palette.success.main,
  fontWeight: 700,
}));

export const FilterLabel = styled(Text)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  flexShrink: 0,
  fontSize: '12px',
  lineHeight: '16px',
}));

export const ExactMatchCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&.Mui-checked': {
    color: theme.palette.primary.main,
  },
}));

export const ExactMatchLabel = styled(FormControlLabel)(({ theme }) => ({
  marginLeft: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    fontFamily: '"Lato", sans-serif',
    color: theme.palette.text.secondary,
  },
}));
